import { utilities as csUtilities, type Types } from '@cornerstonejs/core'

/**
 * Converts canvas coordinates to image coordinates using cornerstone utilities
 */
export const convertCanvasToImageCoords = (
  canvasX: number,
  canvasY: number,
  canvasWidth: number,
  canvasHeight: number,
  viewport: Types.IStackViewport,
  currentImageId: string,
) => {
  // Convert canvas coordinates to world coordinates
  const canvasTopLeft: [number, number] = [canvasX, canvasY]
  const canvasBottomRight: [number, number] = [canvasX + canvasWidth, canvasY + canvasHeight]

  const worldTopLeft = viewport.canvasToWorld(canvasTopLeft)
  const worldBottomRight = viewport.canvasToWorld(canvasBottomRight)

  if (!worldTopLeft || !worldBottomRight) {
    throw new Error('Failed to convert canvas coordinates to world coordinates')
  }

  // Convert world coordinates to image coordinates
  const imageTopLeft = csUtilities.worldToImageCoords(currentImageId, worldTopLeft)
  const imageBottomRight = csUtilities.worldToImageCoords(currentImageId, worldBottomRight)

  if (!imageTopLeft || !imageBottomRight) {
    throw new Error('Failed to convert world coordinates to image coordinates')
  }

  return {
    x: imageTopLeft[0],
    y: imageTopLeft[1],
    width: imageBottomRight[0] - imageTopLeft[0],
    height: imageBottomRight[1] - imageTopLeft[1],
  }
}

/**
 * Converts image coordinates to canvas coordinates using cornerstone utilities
 */
export const convertImageToCanvasCoords = (
  imageX: number,
  imageY: number,
  imageWidth: number,
  imageHeight: number,
  viewport: Types.IStackViewport,
  currentImageId: string,
) => {
  // Get original coordinates from prediction
  const topLeft = [imageX, imageY] as [number, number]
  const topRight = [imageX + imageWidth, imageY] as [number, number]
  const bottomLeft = [imageX, imageY + imageHeight] as [number, number]
  const bottomRight = [imageX + imageWidth, imageY + imageHeight] as [number, number]

  // Convert from image to world coordinates
  const worldTopLeft = csUtilities.imageToWorldCoords(currentImageId, topLeft)
  const worldTopRight = csUtilities.imageToWorldCoords(currentImageId, topRight)
  const worldBottomLeft = csUtilities.imageToWorldCoords(currentImageId, bottomLeft)
  const worldBottomRight = csUtilities.imageToWorldCoords(currentImageId, bottomRight)

  if (!worldTopLeft || !worldTopRight || !worldBottomLeft || !worldBottomRight) {
    throw new Error('Failed to convert image coordinates to world coordinates')
  }

  // Convert from world coordinates to canvas coordinates
  const canvasTopLeft = viewport.worldToCanvas(worldTopLeft)
  const canvasTopRight = viewport.worldToCanvas(worldTopRight)
  const canvasBottomLeft = viewport.worldToCanvas(worldBottomLeft)
  const canvasBottomRight = viewport.worldToCanvas(worldBottomRight)

  if (!canvasTopLeft || !canvasTopRight || !canvasBottomLeft || !canvasBottomRight) {
    throw new Error('Failed to convert world coordinates to canvas coordinates')
  }

  // Calculate width and height in canvas coordinates
  const canvasWidth = canvasTopRight[0] - canvasTopLeft[0]
  const canvasHeight = canvasBottomLeft[1] - canvasTopLeft[1]

  return {
    x: canvasTopLeft[0],
    y: canvasTopLeft[1],
    width: canvasWidth,
    height: canvasHeight,
  }
}
